// Generic per-server drill-down for super-admin engine monitoring.
// Renders Pingora / BM / AD by switching on `engineType` (URL param `id`
// identifies the specific server). OS metric charts are universal; the
// "Application metrics" section is driven by engineConfig.appCharts so
// per-engine specialisation lives in one config file, not here.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Stack,
  Button,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import SpeedIcon from "@mui/icons-material/Speed";
import MemoryIcon from "@mui/icons-material/Memory";
import BoltIcon from "@mui/icons-material/Bolt";
import TimerIcon from "@mui/icons-material/Timer";
import InsightsIcon from "@mui/icons-material/Insights";

import {
  TOKENS,
  PageHeader,
  StatTile,
  SectionCard,
  StatusPill,
  EmptyState,
} from "../design";
import MetricChart from "./MetricChart";
import { getEngineConfig } from "./engineConfig";
import {
  getEngineCluster,
  getEngineTimeseries,
} from "../../../../../services/engineMonitoringService";
import { formatDate } from "../../../../../utils/format";

const TIME_RANGES = [
  { value: "15m", label: "15m" },
  { value: "1h", label: "1h" },
  { value: "6h", label: "6h" },
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
];

const REFRESH_MS = 30_000;

const fmtUptime = (sec) => {
  if (sec == null || Number.isNaN(sec)) return "—";
  const s = Math.floor(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

const fmtPct = (v) => (v == null || Number.isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`);
const fmtNum = (v, digits = 1) =>
  v == null || Number.isNaN(v) ? "—" : Number(v).toFixed(digits);
const fmtBytes = (mb) => {
  if (mb == null || Number.isNaN(mb)) return "—";
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Number(mb).toFixed(0)} MB`;
};
const StatusForServer = ({ row }) => {
  if (!row) return null;
  if (row.stale) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 24,
          px: "10px",
          borderRadius: 999,
          background: TOKENS.dangerBg,
          color: TOKENS.dangerInk,
          border: `1px solid ${TOKENS.dangerBorder}`,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.danger }} />
        Stale
      </Box>
    );
  }
  const h = (row.healthStatus || "").toUpperCase();
  if (h === "HEALTHY") return <StatusPill status="Active" />;
  if (h === "DEGRADED") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 24,
          px: "10px",
          borderRadius: 999,
          background: TOKENS.warningBg,
          color: TOKENS.warningInk,
          border: `1px solid ${TOKENS.warningBorder}`,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.warning }} />
        Degraded
      </Box>
    );
  }
  return <StatusPill status="Pending" />;
};

const TimeRangeSwitcher = ({ value, onChange }) => (
  <Box
    sx={{
      display: "inline-flex",
      border: `1px solid ${TOKENS.border}`,
      borderRadius: `${TOKENS.radiusXs}px`,
      overflow: "hidden",
      background: TOKENS.bgSurface,
    }}
  >
    {TIME_RANGES.map((r) => {
      const active = r.value === value;
      return (
        <Box
          key={r.value}
          onClick={() => onChange(r.value)}
          sx={{
            cursor: "pointer",
            px: 1.25,
            py: 0.5,
            fontSize: 12,
            fontWeight: 600,
            color: active ? TOKENS.primaryInk : TOKENS.fg2,
            background: active ? TOKENS.primaryTint : "transparent",
            borderRight: `1px solid ${TOKENS.border}`,
            "&:last-of-type": { borderRight: "none" },
            "&:hover": { color: TOKENS.fg1 },
          }}
        >
          {r.label}
        </Box>
      );
    })}
  </Box>
);

const KV = ({ label, value, mono = false }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, gap: 2 }}>
    <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3, flexShrink: 0 }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: 13,
        color: TOKENS.fg1,
        fontWeight: 500,
        textAlign: "right",
        fontFamily: mono ? "monospace" : "inherit",
        wordBreak: "break-all",
      }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

const seriesMinMax = (data) => {
  const vals = (data || []).map((p) => p?.v).filter((v) => v != null && !Number.isNaN(v));
  if (vals.length === 0) return [null, null];
  return [Math.min(...vals), Math.max(...vals)];
};

const EngineDrillDownPage = ({ engineType }) => {
  const cfg = getEngineConfig(engineType);
  const navigate = useNavigate();
  const { id } = useParams();

  const [server, setServer] = useState(null);
  const [series, setSeries] = useState({});
  const [range, setRange] = useState("1h");
  // Default auto-refresh ON for 1h+15m to mirror cluster page; OFF for longer
  // windows so we don't surprise operators panning through historical data.
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCluster = useCallback(async () => {
    try {
      const res = await getEngineCluster(engineType);
      const list = res?.data?.engines || [];
      const match = list.find((e) => String(e.id) === String(id));
      setServer(match || null);
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed to load engine data");
    } finally {
      setLoading(false);
    }
  }, [engineType, id]);

  const fetchSeries = useCallback(async () => {
    try {
      const res = await getEngineTimeseries(id, {
        range,
        metrics: "cpu_pct,mem_pct,load_1m,load_5m,load_15m,requests_total,in_flight",
      });
      setSeries(res?.data?.series || {});
    } catch (e) {
      // Keep prior series on failure; surface error banner via state
      setError(e?.message || "Failed to load timeseries");
    }
  }, [id, range]);

  useEffect(() => {
    let iv = null;
    fetchCluster();
    if (autoRefresh && (range === "15m" || range === "1h")) {
      iv = setInterval(() => {
        fetchCluster();
        fetchSeries();
      }, REFRESH_MS);
    }
    return () => {
      if (iv) clearInterval(iv);
    };
  }, [fetchCluster, fetchSeries, autoRefresh, range]);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  // Adjust default auto-refresh when user changes range
  useEffect(() => {
    setAutoRefresh(range === "15m" || range === "1h");
  }, [range]);

  const longRange = range === "24h" || range === "7d";

  const cpuS = useMemo(
    () => [{ label: "CPU %", color: "#0255ED", data: series.cpu_pct || [] }],
    [series.cpu_pct]
  );
  const memS = useMemo(
    () => [{ label: "Memory %", color: "#0f7a42", data: series.mem_pct || [] }],
    [series.mem_pct]
  );
  const loadS = useMemo(
    () => [
      { label: "1m", color: "#6371F7", data: series.load_1m || [] },
      { label: "5m", color: "#8b5cf6", data: series.load_5m || [] },
      { label: "15m", color: "#ef4444", data: series.load_15m || [] },
    ],
    [series.load_1m, series.load_5m, series.load_15m]
  );

  const [cpuMin, cpuMax] = useMemo(() => seriesMinMax(series.cpu_pct), [series.cpu_pct]);

  const headerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <StatusForServer row={server} />
      <TimeRangeSwitcher value={range} onChange={setRange} />
      <FormControlLabel
        sx={{ ml: 1, color: TOKENS.fg2, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
        control={
          <Switch
            size="small"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
        }
        label="Auto-refresh"
      />
      <Button
        onClick={() => {
          fetchCluster();
          fetchSeries();
        }}
        startIcon={<RefreshIcon style={{ width: 16, height: 16 }} />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: 12.5,
          color: TOKENS.fg1,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusXs}px`,
          px: 1.5,
          py: 0.5,
          "&:hover": { background: TOKENS.bgSunken },
        }}
      >
        Refresh
      </Button>
    </Stack>
  );

  const title = server?.hostname || (loading ? "Loading…" : "Server not found");
  const subtitle = server
    ? `${cfg.displayName} · ${server.ip || "—"} · v${server.version || "—"}`
    : cfg.displayName;

  return (
    <Box>
      <Helmet title={`${cfg.displayName} · ${server?.hostname || id}`} />
      <Box sx={{ mb: 1 }}>
        <Button
          onClick={() => navigate(`/super/engines/${cfg.slug}`)}
          startIcon={<ArrowBackIcon style={{ width: 16, height: 16 }} />}
          sx={{
            textTransform: "none",
            color: TOKENS.fg2,
            fontSize: 12.5,
            fontWeight: 500,
            px: 0,
            "&:hover": { background: "transparent", color: TOKENS.primary },
          }}
        >
          Back to {cfg.displayName} cluster
        </Button>
      </Box>
      <PageHeader title={title} subtitle={subtitle} actions={headerActions} />

      {error ? (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: `${TOKENS.radiusXs}px`,
            background: TOKENS.dangerBg,
            border: `1px solid ${TOKENS.dangerBorder}`,
            color: TOKENS.dangerInk,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Failed to load engine data. {error}</span>
          <Button
            size="small"
            onClick={() => {
              fetchCluster();
              fetchSeries();
            }}
            sx={{ textTransform: "none", color: TOKENS.dangerInk, fontWeight: 600 }}
          >
            Retry
          </Button>
        </Box>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<SpeedIcon fontSize="small" />}
            iconBg={TOKENS.infoBg}
            iconInk={TOKENS.infoInk}
            value={fmtPct(server?.cpuUsage)}
            label="CPU %"
            sub={
              cpuMin != null
                ? `${range} min ${fmtPct(cpuMin)} · max ${fmtPct(cpuMax)}`
                : `${server?.cpuCores || "?"} cores`
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<MemoryIcon fontSize="small" />}
            iconBg={TOKENS.successBg}
            iconInk={TOKENS.successInk}
            value={fmtPct(server?.memoryUsage)}
            label="Memory %"
            sub={
              server
                ? `${fmtBytes(server.memoryTotal)} total · ${fmtBytes(server.memoryFree)} free`
                : null
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<BoltIcon fontSize="small" />}
            iconBg="#FEF3C7"
            iconInk="#92400E"
            value={fmtNum(server?.loadAvg1, 2)}
            label="Load 1m"
            sub={
              server
                ? `5m ${fmtNum(server.loadAvg5, 2)} · 15m ${fmtNum(server.loadAvg15, 2)}`
                : null
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon={<TimerIcon fontSize="small" />}
            iconBg={TOKENS.primaryTint}
            iconInk={TOKENS.primaryInk}
            value={fmtUptime(server?.uptime)}
            label="Uptime"
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <SectionCard title="OS metrics" description={`${range} window`}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <MetricChart series={cpuS} height={220} unit="%" title="CPU %" longRange={longRange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricChart series={memS} height={220} unit="%" title="Memory %" longRange={longRange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricChart series={loadS} height={220} title="Load avg" longRange={longRange} />
            </Grid>
          </Grid>
        </SectionCard>
      </Box>

      <Box sx={{ mb: 2 }}>
        <SectionCard title="Application metrics" description={`${range} window`}>
          {cfg.appCharts.length === 0 ? (
            <EmptyState
              icon={<InsightsIcon />}
              title={`${cfg.displayName} doesn't expose app counters yet`}
              body="Only OS metrics are shown above. Add Prometheus counters in the engine to populate this section."
            />
          ) : (
            <Grid container spacing={2}>
              {cfg.appCharts.map((c) => {
                const data = series[c.key] || [];
                const chartSeries = [{ label: c.label, color: c.color, data }];
                const cols = cfg.appCharts.length === 1 ? 12 : 6;
                return (
                  <Grid item xs={12} md={cols} key={c.key}>
                    <MetricChart
                      series={chartSeries}
                      height={220}
                      unit={c.unit || ""}
                      title={c.label}
                      longRange={longRange}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </SectionCard>
      </Box>

      <SectionCard title="Server details">
        {!server && loading ? (
          <Typography sx={{ fontSize: 13, color: TOKENS.fg3 }}>Loading…</Typography>
        ) : !server ? (
          <EmptyState
            icon={<InsightsIcon />}
            title="Server not found"
            body={`No ${cfg.displayName} server with id ${id} is currently reporting.`}
          />
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <KV label="ID" value={server.id} mono />
              <KV label="Hostname" value={server.hostname} />
              <KV label="IP" value={server.ip} mono />
              <KV label="Version" value={server.version} />
              <KV label="OS" value={server.os} />
              <KV label="CPU cores" value={server.cpuCores} />
              <KV label="Sites loaded" value={server.sitesLoaded ?? "—"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <KV label="First seen" value={formatDate(server.firstSeen || server.createdAt) || "—"} />
              <KV label="Last heartbeat" value={formatDate(server.lastHeartbeat) || "—"} />
              <KV
                label="Age"
                value={server.ageSeconds != null ? `${fmtUptime(server.ageSeconds)} ago` : "—"}
              />
              <KV label="Health" value={server.healthStatus || "—"} />
              {server.lastError ? <KV label="Last error" value={server.lastError} /> : null}
            </Grid>
          </Grid>
        )}
      </SectionCard>
    </Box>
  );
};

export default EngineDrillDownPage;
